import React from 'react'

import { User } from '@guiker/authentication-shared'
import { ButtonContainer, ButtonWithLoader, FullScreenSpinner, useNavigate } from '@guiker/components-library'
import { ApiForm, useMutation, useQuery, yupResolver } from '@guiker/react-framework'
import { CountryCode } from '@guiker/shared-framework'
import { AboutYouFormContent } from '@guiker/user-components'
import { useAuthenticatedUserApiClient } from '@guiker/user-context'
import { AuthenticatedApi, LegalProfile, Personal } from '@guiker/user-shared'

import { useAuthenticationContext } from '../../hooks'
import { useT } from '../../i18n'

const schema = AuthenticatedApi.Schema.updateLegalProfileSchema

const getDefault = (args: { user: User; legalProfile: LegalProfile | null }) => {
  const { user, legalProfile } = args
  if (!legalProfile) {
    return {
      dateOfBirth: user.birthDate,
      legalName: { firstName: user.firstName, lastName: user.lastName },
      emailAddress: user.emailAddress,
      phone: { country: CountryCode.CA, number: user.phoneNumber },
    }
  } else {
    return legalProfile.personal
  }
}

const UpdateProfileForm: React.FC = () => {
  const { tBase } = useT({ screenName: 'screens.profile' })

  const { user } = useAuthenticationContext()
  const resolver = yupResolver(schema)
  const navigate = useNavigate()
  const userApiClient = useAuthenticatedUserApiClient()

  const {
    data: legalProfile,
    isLoading: isLoadingLegalProfile,
    refetch,
  } = useQuery([`legal-profile-${user.id}`], () =>
    userApiClient.readOneLegalProfile({ pathParams: { userId: user.id } }),
  )

  const { mutate: onSubmit, isLoading } = useMutation(
    (payload: Personal) => {
      return userApiClient.upsertLegalProfile({
        pathParams: { userId: user.id },
        payload,
      })
    },
    {
      onSuccess: () => {
        refetch()
        navigate('/success')
      },
    },
  )

  if (isLoadingLegalProfile) return <FullScreenSpinner />

  return (
    <ApiForm
      formName='updateProfile'
      formOptions={{ resolver, defaultValues: getDefault({ user, legalProfile }) }}
      onSubmit={onSubmit}
    >
      <AboutYouFormContent includeEmail defaultValue={getDefault({ user, legalProfile })} />
      <ButtonContainer>
        <ButtonWithLoader type='submit' isLoading={isLoading}>
          {tBase('actions.save')}
        </ButtonWithLoader>
      </ButtonContainer>
    </ApiForm>
  )
}

export { UpdateProfileForm }
